"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { CartIcon } from "@/components/shop/CartIcon";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        {[0, 1, 2, 3].map(i => <div key={i} data-index={i} className="h-9 w-20 bg-muted/60 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="relative inline-flex items-center px-3 py-2 text-[15px] font-default font-medium text-foreground tracking-wide bg-transparent hover:bg-transparent focus:bg-transparent group data-[state=open]:bg-transparent">
                  <span className="relative">
                    {item.label}
                    <span className="absolute left-0 -bottom-1 h-[2px] w-0 bg-primary transition-all duration-[280ms] ease-out group-hover:w-full" />
                  </span>
                </NavigationMenuTrigger>
                <NavigationMenuContent className="min-w-[200px] bg-background border border-border shadow-lg rounded-lg p-2">
                  <ul className="flex flex-col gap-1">
                    {item.children.map((child, childIdx) => <li key={child.id} data-index={childIdx}>
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="block px-4 py-2 text-sm font-medium text-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors">
                            {child.label}
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="relative inline-flex items-center px-3 py-2 text-[15px] font-default font-medium text-foreground tracking-wide bg-transparent hover:bg-transparent focus:bg-transparent group" style={{
            letterSpacing: "0.02em"
          }}>
                  <span className="relative">
                    {item.label}
                    <span className="absolute left-0 -bottom-1 h-[2px] w-0 bg-primary transition-all duration-[280ms] ease-out group-hover:w-full" />
                  </span>
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNavConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4">
        {[0, 1, 2, 3].map(i => <div key={i} data-index={i} className="h-10 w-3/4 bg-secondary-foreground/10 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-2">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col">
          <Link to={item.href || "#"} onClick={onNavigate} className="font-serif text-3xl font-semibold text-secondary-foreground tracking-tight py-2 border-b border-secondary-foreground/15 hover:text-primary transition-colors duration-200" style={{
        letterSpacing: "-0.015em"
      }}>
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <div className="flex flex-col pl-6 mt-2 gap-2">
              {item.children.map((child, childIdx) => <Link key={child.id} data-index={childIdx} to={child.href || "#"} onClick={onNavigate} className="font-sans text-lg font-medium text-secondary-foreground/80 hover:text-primary transition-colors duration-200 py-1">
                  {child.label}
                </Link>)}
            </div>}
        </div>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1592" id="forever-fresh-header" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${scrolled ? "bg-background/80 backdrop-blur-md border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className="hidden lg:flex absolute left-3 top-1/2 -translate-y-1/2 items-center pointer-events-none" aria-hidden="true">
        <span className="font-mono text-[10px] text-foreground/60 whitespace-nowrap" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)",
        letterSpacing: "0.18em"
      }}>
          MISSION // INDEX
        </span>
      </div>

      <div className="mx-auto w-full max-w-[1400px] px-5 md:px-10 lg:px-14">
        <div className="flex h-20 items-center justify-between gap-4">
          <div className="flex items-center flex-shrink-0 lg:w-1/4">
            <Link to="/" aria-label="Forever Fresh — Home" className="inline-flex items-center">
              <WvcLogo className="h-8 w-auto" />
            </Link>
          </div>

          <div className="hidden lg:flex flex justify-center max-w-fit">
            <MenuProvider menu_id="22" className="header-menu">
              <DesktopNavConsumer />
            </MenuProvider>
          </div>

          <div className="flex items-center justify-end gap-3 lg:w-1/4">
            <div className="hidden xl:flex flex-col items-end">
              
            </div>

            <CartIcon variant="ghost" size="icon" iconSize="size-5" className="text-foreground hover:bg-muted rounded-lg" />

            <div className="lg:hidden">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:bg-muted rounded-lg">
                    <Menu className="size-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-secondary text-secondary-foreground border-l border-secondary-foreground/15 w-full sm:max-w-md p-0 flex flex-col">
                  <div className="flex items-center justify-between px-6 pt-6 pb-4">
                    <span className="font-mono text-[11px] text-secondary-foreground/70" style={{
                    letterSpacing: "0.18em"
                  }}>
                      MISSION // INDEX
                    </span>
                  </div>

                  <div className="flex-1 overflow-y-auto px-6 pb-6">
                    <MenuProvider menu_id="22" className="mobile-menu">
                      <MobileNavConsumer onNavigate={() => setMobileOpen(false)} />
                    </MenuProvider>

                    <div className="mt-10">
                      <span className="inline-flex items-center rounded-lg bg-chart-2 text-background font-mono text-[10px] px-3 py-1.5 border border-dashed border-background/70" style={{
                      letterSpacing: "0.12em"
                    }}>
                        FOREVER FRESH • KEEPING AWARENESS FRESH
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-secondary-foreground/15 overflow-hidden py-3 bg-secondary">
                    <div className="whitespace-nowrap font-mono text-[11px] text-primary" style={{
                    letterSpacing: "0.18em",
                    animation: "vv-ticker 60s linear infinite"
                  }}>
                      FOREVER FRESH • KEEPING AWARENESS FRESH • SUPPORT THE CAUSE • FOREVER FRESH • KEEPING AWARENESS FRESH • SUPPORT THE CAUSE •
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes vv-ticker {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </section>;
}